<script setup>
import { computed, ref, onMounted } from 'vue'
import useLinkAction from './useLinkAction';
import { useStore } from 'vuex';
import { DsButton } from '@coloplast/design-system';
import Events from 'shared/settings/events';
import { extractUrlParameters, removeQueryStringParameter } from 'shared/services/windowLocationService';

const props = defineProps({
	vm: Object,
	url: String,
	variant: [Object, String],
	linkSettings: [Object, String],
	iconEnd: String,
	size: String,
	iconStart: String,
	buttonVariant: {
		type: String,
		default: 'link'
	},
});

const store = useStore();
const isLoaded = ref(false)
const searchParameters = ref({})
const loginModalState = computed(() => store.state.globalCheckout.isLoginModalShow);
const currency = computed(() => store.getters.getCurrency);
const marketId = computed(() => store.getters.getMarketId);
const isUserLoggedIn = computed(() => store.getters.getIsUserLoggedIn);
const hasUrlParameters = computed(() => searchParameters.value?.variant && searchParameters.value?.quantity && searchParameters.value?.accountregistrationtype === 'AddToBasket');


onMounted(() => {
	isLoaded.value = true;

	// the case where the user was redirected to login / sign up when adding a product to the basket
	if (window.location.search !== '') {
		searchParameters.value = extractUrlParameters(window.location.search);

		if ( searchParameters.value.variant === props.variant?.Code) {
			// the code will only reach that when the user is redirected from the B2C
			if (isUserLoggedIn.value && hasUrlParameters.value) {

				const payload = {
					Code: searchParameters.value?.variant,
					Quantity: searchParameters.value?.quantity,
					MarketId: marketId.value,
					DefaultCurrency: currency.value,
					isSample: props.variant.IsSample,
					isCuttingServiceChecked: props.variant.IsCuttingServiceChecked,
				};

				store.dispatch('addItemToBasket', payload)
				.then(() => {
					const event = new CustomEvent(Events.ACTIVATE_FLY_OUT_BASKET, {});
					document.dispatchEvent(event);
					clearSearchParameters();
				})
			}
		}
	}
});

const { addSampleAction, quickSampleAction } = useLinkAction();

// for SSR
const linkSettings = computed(() => {
	return props.linkSettings.BaseViewModel || props.linkSettings
})
const variant = computed(() => {
	return props.variant.BaseViewModel || props.variant
})

// type props.linkSettings.LinkBehavior = 'AddSample' | 'AddSampleGoToCheckout' | 'RedirectToLink'
function clickHandler(variant) {
	if (linkSettings.value.LoginRequiredForAddToBasket) {
		if (props.variant) {
			// if the variant is defined, make sure to update the store so the login modal can construct the correct return url
			store.commit('setSelectedVariantInfo', {
				selectedVariantCode: props.variant.Code,
				quantity: props.variant.Quantity,
				isSample: props.variant.IsSample,
				isCut: props.variant.IsCuttingServiceChecked,
			});
		}
		store.commit('globalCheckout/setLoginModalShow', !loginModalState.value);
	} else {
		action(variant)
	}
}

function clearSearchParameters() {
	removeQueryStringParameter('variant');
	removeQueryStringParameter('quantity');
	removeQueryStringParameter('isSample');
	removeQueryStringParameter('iscut');
	removeQueryStringParameter('accountregistrationtype');

	searchParameters.value = {};
}

const action = {
	AddSample: addSampleAction,
	AddSampleGoToCheckout: quickSampleAction,
}[linkSettings.value.LinkBehavior];

</script>
<template>
	<template v-if="!isLoaded">
		<slot></slot>
	</template>
	<template v-else>
		<template v-if="linkSettings.LinkBehavior === 'RedirectToLink' || linkSettings.LinkBehavior === 'SignInLink'">
			<DsButton
					  v-if="url"
					  :href="url"
					  :hreflang="linkSettings.Language"
					  :target="linkSettings.LinkTarget"
					  :aria-label="linkSettings.AriaLabel"
					  :variant="buttonVariant"
					  :icon-end="iconEnd"
					  :icon-start="iconStart"
					  data-testid="generalLink">
				{{ linkSettings.LinkLabel }}
			</DsButton>
		</template>
		<DsButton v-else
				  :clickHandler="() => clickHandler(variant)"
				  :aria-label="linkSettings.AriaLabel"
				  :variant="buttonVariant"
				  :icon-end="iconEnd"
				  :icon-start="iconStart"
				  :size="size"
				  data-testid="generalButton">
			{{ linkSettings.LinkLabel }}
		</DsButton>
	</template>
</template>

